import React from 'react';
import { Layout, ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { fluentDesignTokens } from './styles/fluentDesignTokens';
import './App.css';
import TopNavigation from './components/Navigation/TopNavigation';
import OperationsOverviewPage from './pages/OperationsOverviewPage';
import OrganizationWorkbenchPage from './pages/OrganizationWorkbenchPage';
import QualificationsPage from './pages/QualificationsPage';
import QualificationMatrixPage from './pages/QualificationMatrixPage';
import OperationsPage from './pages/OperationsPage';
import OperationTypesPage from './pages/OperationTypesPage';
import BatchManagementV4Page from './pages/BatchManagementV4Page';
import BatchManagementWorkbenchV2Page from './pages/BatchManagementWorkbenchV2Page';
import PersonnelSchedulingPage from './pages/PersonnelSchedulingPage';
import SolverV4Page from './pages/SolverV4Page';
import SolverV5Page from './pages/SolverV5Page';
import ShiftDefinitionsPage from './pages/ShiftDefinitionsPage';
import EmployeeSchedulePage from './pages/employee-schedule/EmployeeSchedulePage';
import UiKitShowcasePage from './pages/UiKitShowcasePage';
import ProcessTemplatesV3Page from './pages/ProcessTemplatesV3Page';
import ProductionSchedulingPage from './pages/ProductionSchedulingPage';
import ProcessFlowBuilderPage from './pages/ProcessFlowBuilderPage';
import TemplateListPage from './pages/TemplateListPage';
import EquipmentStateMachinePage from './pages/EquipmentStateMachinePage';
import DerivableLibraryPage from './pages/DerivableLibraryPage';
import PsResourceMasterPage from './pages/PsResourceMasterPage';
import PsFactorySandtablePage from './pages/PsFactorySandtablePage';
import ProdCipTopologyPage from './pages/ProdCipTopologyPage';
import ProdCipLoopsPage from './pages/ProdCipLoopsPage';
import ProdFacilityMasterPage from './pages/ProdFacilityMasterPage';
import ProdProcessTemplatePage from './pages/ProdProcessTemplatePage';
import ProdWorldStatePage from './pages/ProdWorldStatePage';
import ProdProcessTemplateContractPage from './pages/ProdProcessTemplateContractPage';
import EquipmentManagementPage from './pages/EquipmentManagementPage';
import RosterLeadershipCockpitPage from './pages/roster/RosterLeadershipCockpitPage';
import RosterTriagePage from './pages/roster-triage';
import LoginPage from './pages/LoginPage';
import RoleManagementPage from './pages/governance/RoleManagementPage';
import UserManagementPage from './pages/governance/UserManagementPage';
import PermissionCatalogPage from './pages/governance/PermissionCatalogPage';
import SiteManagementPage from './pages/governance/SiteManagementPage';
import PlatformIdentityManagementPage from './pages/governance/PlatformIdentityManagementPage';
import HolidayCalendarManagementPage from './pages/governance/HolidayCalendarManagementPage';
import {
  PRODUCTION_AUTO_SCHEDULING_LEGACY_REDIRECTS,
  PRODUCTION_AUTO_SCHEDULING_ROUTES,
} from './productionAutoSchedulingRoutes';
import { MVP_REDIRECTS } from './legacyRouteRedirects';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute, { isAuthEnforced } from './components/auth/ProtectedRoute';
import { WxbEmpty, WxbSpinner } from './components/wxb-ui';
import {
  canSeeNavigationItem,
  resolveProductionAutoSchedulingRootTarget,
} from './components/Navigation/navigationModel';
import {
  getSitePublicIdFromPagePath,
  toSitePagePath,
  toSiteRoutePattern,
} from './siteRoutes';

const { Content } = Layout;

const BatchManagementV5Page = React.lazy(() => import('./pages/BatchManagementV5Page'));
const ProcessTemplatesV5Page = React.lazy(() => import('./pages/ProcessTemplatesV5Page'));

const ProcessTemplateLegacyRedirect: React.FC = () => {
  const location = useLocation();
  const { templateId, sitePublicId } = useParams<{
    templateId?: string;
    sitePublicId?: string;
  }>();
  const target = templateId ? `/process-templates/${templateId}` : '/process-templates';

  return (
    <Navigate
      to={`${toSitePagePath(sitePublicId ?? '', target)}${location.search}`}
      replace
    />
  );
};

const LocationPreservingRedirect: React.FC<{ to: string }> = ({ to }) => {
  const location = useLocation();
  const { sitePublicId } = useParams<{ sitePublicId?: string }>();

  return (
    <Navigate
      to={`${toSitePagePath(sitePublicId ?? '', to)}${location.search}${location.hash}`}
      replace
    />
  );
};

const ProductionAutoSchedulingRootRedirect: React.FC = () => {
  const location = useLocation();
  const { user, currentSite, hasPermission, loading } = useAuth();

  if (loading) {
    return <WxbSpinner tip="正在确定自动排产入口…" />;
  }

  if (!user && isAuthEnforced()) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const target = resolveProductionAutoSchedulingRootTarget((requiredPermission) => (
    canSeeNavigationItem(
      requiredPermission,
      Boolean(user),
      isAuthEnforced(),
      hasPermission,
    )
  ));

  if (!currentSite) {
    return <WxbEmpty description="当前账号没有可用的 Site" />;
  }
  return (
    <Navigate
      to={`${toSitePagePath(currentSite.publicId, target)}${location.search}${location.hash}`}
      replace
    />
  );
};

const LegacySitePageRedirect: React.FC = () => {
  const location = useLocation();
  const { user, currentSite, loading } = useAuth();
  if (loading) return <WxbSpinner tip="正在进入 Site…" />;
  if (!user && isAuthEnforced()) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (!currentSite) {
    return <WxbEmpty description="当前账号没有可用的 Site" />;
  }
  if (getSitePublicIdFromPagePath(location.pathname)) {
    return (
      <Navigate
        to={toSitePagePath(currentSite.publicId, '/operations-overview')}
        replace
      />
    );
  }
  const logicalPath = location.pathname === '/' || location.pathname === '/dashboard'
    ? '/operations-overview'
    : location.pathname;
  return (
    <Navigate
      to={`${toSitePagePath(currentSite.publicId, logicalPath)}${location.search}${location.hash}`}
      replace
    />
  );
};

const AppLayout: React.FC = () => {
  const location = useLocation();
  const {
    currentSite,
    memberships,
    selectSite,
  } = useAuth();
  const requestedSitePublicId = getSitePublicIdFromPagePath(location.pathname);
  const requestedMembership = requestedSitePublicId
    ? memberships.find(
      (membership) => (
        membership.membershipStatus === 'ACTIVE'
        && membership.site.publicId === requestedSitePublicId
      ),
    )
    : null;
  const [siteRouteError, setSiteRouteError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (
      !requestedSitePublicId
      || !requestedMembership
      || currentSite?.publicId === requestedSitePublicId
    ) {
      setSiteRouteError(null);
      return;
    }
    let cancelled = false;
    setSiteRouteError(null);
    void selectSite(requestedSitePublicId).catch((error) => {
      if (!cancelled) {
        setSiteRouteError(error instanceof Error ? error.message : 'Site 切换失败');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    currentSite?.publicId,
    requestedMembership,
    requestedSitePublicId,
    selectSite,
  ]);

  const siteRouteBlocked = requestedSitePublicId && !requestedMembership;
  const siteRouteSwitching = (
    requestedSitePublicId
    && requestedMembership
    && currentSite?.publicId !== requestedSitePublicId
  );

  return (
    <ConfigProvider
    locale={zhCN}
    theme={{
      token: {
        colorPrimary: fluentDesignTokens.colors.accent,
        borderRadius: Number.parseInt(fluentDesignTokens.borderRadius.md),
        fontFamily: `${fluentDesignTokens.typography.fontFamily.zh}, ${fluentDesignTokens.typography.fontFamily.en}`,
        fontSize: Number.parseInt(fluentDesignTokens.typography.fontSize.body),
      },
    }}
  >
    <Layout className="main-layout">
      <TopNavigation />

      <Content className="fluent-content">
        {siteRouteBlocked ? (
          <WxbEmpty description="该 Site 不存在、不可用，或你不是其成员" />
        ) : siteRouteError ? (
          <WxbEmpty description={siteRouteError} />
        ) : siteRouteSwitching ? (
          <WxbSpinner tip="正在切换 Site…" />
        ) : (
        <Routes key={currentSite?.publicId ?? 'no-site'}>
          <Route
            path={toSiteRoutePattern('/')}
            element={<LocationPreservingRedirect to="/operations-overview" />}
          />
          <Route
            path={toSiteRoutePattern('/dashboard')}
            element={<LocationPreservingRedirect to="/operations-overview" />}
          />
          <Route
            path={toSiteRoutePattern('/operations-overview')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="SYSTEM_DASHBOARD_READ">
                <OperationsOverviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/my-schedule')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="ROSTER_SCHEDULE_READ">
                <EmployeeSchedulePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/organization-workbench')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="MASTER_EMPLOYEE_READ">
                <OrganizationWorkbenchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/qualifications')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="MASTER_QUALIFICATION_READ">
                <QualificationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/qualification-matrix')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="MASTER_QUALIFICATION_READ">
                <QualificationMatrixPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/operations')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="MASTER_OPERATION_READ">
                <OperationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/operation-types')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="MASTER_OPERATION_READ">
                <OperationTypesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/process-templates')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="APS_TEMPLATE_READ">
                <ProcessTemplatesV3Page />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/process-templates/:templateId')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="APS_TEMPLATE_READ">
                <ProcessTemplatesV3Page />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/process-templates-v5')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="APS_TEMPLATE_READ">
                <React.Suspense fallback={<WxbSpinner tip="正在加载工艺模版 V5" />}>
                  <ProcessTemplatesV5Page />
                </React.Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/process-templates-v5/:templateId')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="APS_TEMPLATE_READ">
                <React.Suspense fallback={<WxbSpinner tip="正在加载工艺模版 V5" />}>
                  <ProcessTemplatesV5Page />
                </React.Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/production-scheduling')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="APS_TEMPLATE_READ">
                <ProductionSchedulingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/process-flow-builder')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="APS_TEMPLATE_READ">
                <ProcessFlowBuilderPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/process-flow-templates')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="APS_TEMPLATE_READ">
                <TemplateListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern(PRODUCTION_AUTO_SCHEDULING_ROUTES.equipmentStateModel)}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="MASTER_RESOURCE_READ">
                <EquipmentStateMachinePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern(PRODUCTION_AUTO_SCHEDULING_ROUTES.facilityMaster)}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="MASTER_RESOURCE_READ">
                <ProdFacilityMasterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern(PRODUCTION_AUTO_SCHEDULING_ROUTES.processTemplates)}
            element={<ProtectedRoute allowAnonymousInShadow requiredPermission="APS_TEMPLATE_READ"><ProdProcessTemplatePage /></ProtectedRoute>}
          />
          <Route path={toSiteRoutePattern(PRODUCTION_AUTO_SCHEDULING_ROUTES.worldState)} element={<ProtectedRoute allowAnonymousInShadow requiredPermission="MASTER_RESOURCE_READ"><ProdWorldStatePage /></ProtectedRoute>} />
          <Route path={toSiteRoutePattern(PRODUCTION_AUTO_SCHEDULING_ROUTES.processTemplateContract)} element={<ProtectedRoute allowAnonymousInShadow requiredPermission="APS_TEMPLATE_READ"><ProdProcessTemplateContractPage /></ProtectedRoute>} />
          <Route
            path={toSiteRoutePattern('/derivable-library')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="APS_TEMPLATE_READ">
                <DerivableLibraryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/ps-resource-master')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="APS_TEMPLATE_READ">
                <PsResourceMasterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/factory-sandtable')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="APS_TEMPLATE_READ">
                <PsFactorySandtablePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern(PRODUCTION_AUTO_SCHEDULING_ROUTES.cipTopology)}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="MASTER_RESOURCE_READ">
                <ProdCipTopologyPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern(PRODUCTION_AUTO_SCHEDULING_ROUTES.cipLoops)}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="MASTER_RESOURCE_READ">
                <ProdCipLoopsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern(PRODUCTION_AUTO_SCHEDULING_ROUTES.root)}
            element={<ProductionAutoSchedulingRootRedirect />}
          />
          {Object.entries(PRODUCTION_AUTO_SCHEDULING_LEGACY_REDIRECTS).map(([path, target]) => (
            <Route
              key={path}
              path={toSiteRoutePattern(path)}
              element={<LocationPreservingRedirect to={target} />}
            />
          ))}
          <Route path={toSiteRoutePattern('/process-templates-v2')} element={<ProcessTemplateLegacyRedirect />} />
          <Route path={toSiteRoutePattern('/process-templates-v2/:templateId')} element={<ProcessTemplateLegacyRedirect />} />
          <Route
            path={toSiteRoutePattern('/batch-management-v4')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="APS_BATCH_READ">
                <BatchManagementV4Page />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/batch-management-v5')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="APS_BATCH_READ">
                <React.Suspense fallback={<WxbSpinner tip="正在加载批次管理 V5" />}>
                  <BatchManagementV5Page />
                </React.Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/batch-management-workbench-v2')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="APS_BATCH_READ">
                <BatchManagementWorkbenchV2Page />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/personnel-scheduling')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="ROSTER_SCHEDULE_READ">
                <PersonnelSchedulingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/roster/triage')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="ROSTER_SCHEDULE_READ">
                <RosterTriagePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/roster/leadership-cockpit')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="ROSTER_COCKPIT_READ">
                <RosterLeadershipCockpitPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/solver-v4')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="SOLVER_RUN_READ">
                <SolverV4Page />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/solver-v5')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="SOLVER_RUN_READ">
                <SolverV5Page />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/shift-definitions')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="MASTER_SHIFT_DEF_READ">
                <ShiftDefinitionsPage />
              </ProtectedRoute>
            }
          />
          <Route path={toSiteRoutePattern('/process-templates-v3')} element={<ProcessTemplateLegacyRedirect />} />
          <Route path={toSiteRoutePattern('/process-templates-v3/:templateId')} element={<ProcessTemplateLegacyRedirect />} />
          <Route
            path={toSiteRoutePattern('/equipment-management')}
            element={
              <ProtectedRoute allowAnonymousInShadow requiredPermission="MASTER_RESOURCE_READ">
                <EquipmentManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/governance/sites"
            element={
              <ProtectedRoute requiredPlatformPermission="PLATFORM_SITE_READ">
                <SiteManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/governance/identities"
            element={
              <ProtectedRoute requiredPlatformPermission="PLATFORM_IDENTITY_READ">
                <PlatformIdentityManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/governance/holiday-calendar"
            element={
              <ProtectedRoute requiredPlatformPermission="PLATFORM_SITE_CONFIGURE">
                <HolidayCalendarManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/governance/roles')}
            element={
              <ProtectedRoute requiredPermission="GOVERNANCE_ROLE_READ">
                <RoleManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/governance/users')}
            element={
              <ProtectedRoute requiredPermission="GOVERNANCE_USER_READ">
                <UserManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/governance/permissions')}
            element={
              <ProtectedRoute requiredPermission="GOVERNANCE_ROLE_READ">
                <PermissionCatalogPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={toSiteRoutePattern('/ui-kit')}
            element={
              <ProtectedRoute allowAnonymousInShadow>
                <UiKitShowcasePage />
              </ProtectedRoute>
            }
          />
          {Object.entries(MVP_REDIRECTS).map(([path, target]) => (
            <Route
              key={path}
              path={toSiteRoutePattern(path)}
              element={<LocationPreservingRedirect to={target} />}
            />
          ))}
          <Route path="*" element={<LegacySitePageRedirect />} />
        </Routes>
        )}
      </Content>
    </Layout>
    </ConfigProvider>
  );
};

const App: React.FC = () => (
  <AuthProvider>
    <Router>
      <Routes>
        {/* 登录页在 AppLayout 之外，不套顶部导航 */}
        <Route path="/login" element={<LoginPage />} />
        {/* 其余路由统一经 ProtectedRoute 守卫，再进 AppLayout。
            默认整站强制登录；仅显式设置 REACT_APP_AUTH_ENFORCE=false 时，
            allowAnonymousInShadow 才为本地兼容场景放行匿名访问。
            敏感页（/governance/*）仍由 requiredPermission 做二次门禁。 */}
        <Route
          path="/*"
          element={
            <ProtectedRoute allowAnonymousInShadow>
              <AppLayout />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  </AuthProvider>
);

export default App;
